import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginWithPassword = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data?.token) localStorage.setItem("aw_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    if (res.data?.token) localStorage.setItem("aw_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("aw_token");
    setUser(null);
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    if (user.roles?.includes("admin")) return true;
    return user.roles?.some((r) => roles.includes(r));
  };

  const isInternal = () => hasRole("intern_delivery", "intern_architekt", "intern_audit");
  const isCustomer = () => hasRole("kunde_entscheider", "kunde_mitwirkend") && !isInternal();

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, loginWithPassword, register, logout, hasRole, isInternal, isCustomer, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
